import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown } from 'lucide-react';
import api from '../lib/api';

/**
 * PioneerNavLink — top-of-funnel scarcity teaser for the public nav.
 *
 * Renders "Pioneers" with a pulsing traffic-light dot next to it,
 * reinforcing scarcity without leaking the actual claimed count.
 *
 * Tiers (synced with PioneerAvailabilityBadge):
 *   - GREEN   (0 – 100 claimed)
 *   - YELLOW  (101 – 200 claimed)
 *   - RED     (201 – 249 claimed)
 *   - CLOSED  (>= 250 claimed) — dot goes muted slate; link text stays "Pioneers"
 */
const tierFor = (claimed) => {
  const n = Number(claimed) || 0;
  if (n >= 250) return { key: 'closed', dot: 'bg-slate-400', glow: 'rgba(148,163,184,0.35)', srLabel: 'Pioneer Wall — closed' };
  if (n >= 201) return { key: 'red', dot: 'bg-red-500', glow: 'rgba(239,68,68,0.55)', srLabel: 'Pioneer Wall — final round' };
  if (n >= 101) return { key: 'yellow', dot: 'bg-amber-400', glow: 'rgba(234,179,8,0.55)', srLabel: 'Pioneer Wall — limited' };
  return { key: 'green', dot: 'bg-emerald-400', glow: 'rgba(52,211,153,0.55)', srLabel: 'Pioneer Wall — open' };
};

// Both nav variants (desktop + mobile sheet) mount on the same page; share one
// in-flight request per page load instead of fetching the wall once per mount.
let wallFetch = null;
const fetchWallClaimed = () => {
  if (!wallFetch) {
    wallFetch = api
      .get('/pioneers/wall')
      .then((r) => r.data?.claimed ?? 0)
      .catch(() => {
        wallFetch = null; // let a later mount retry after a failure
        return null;
      });
  }
  return wallFetch;
};

const PioneerNavLink = ({ variant = 'desktop' }) => {
  const [claimed, setClaimed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchWallClaimed().then((n) => {
      // null = fetch failed; keep tier=green as a sensible default
      if (!cancelled && n !== null) setClaimed(n);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const t = tierFor(claimed);

  if (variant === 'mobile') {
    return (
      <Link
        to="/pioneers"
        className="flex items-center justify-between gap-3 py-3 px-2 text-platinum hover:text-gold transition-colors border-b border-platinum/10"
        data-testid="nav-pioneers-mobile"
        data-tier={t.key}
        aria-label={t.srLabel}
      >
        <span className="flex items-center gap-2 font-semibold">
          <Crown size={16} className="text-gold" />
          Pioneers
        </span>
        <span className="relative flex w-2.5 h-2.5" aria-hidden="true">
          <span className={`motion-safe:animate-ping absolute inline-flex w-full h-full rounded-full opacity-70 ${t.dot}`} />
          <span
            className={`relative inline-flex rounded-full w-full h-full ${t.dot}`}
            style={{ boxShadow: `0 0 12px ${t.glow}` }}
          />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/pioneers"
      className="group inline-flex items-center gap-2 text-platinum hover:text-gold transition-colors font-semibold"
      data-testid="nav-pioneers"
      data-tier={t.key}
      aria-label={t.srLabel}
    >
      <Crown size={14} className="text-gold/80 group-hover:text-gold transition-colors" />
      <span>Pioneers</span>
      <span className="relative flex w-2 h-2" aria-hidden="true">
        <span className={`motion-safe:animate-ping absolute inline-flex w-full h-full rounded-full opacity-70 ${t.dot}`} />
        <span
          className={`relative inline-flex rounded-full w-full h-full ${t.dot}`}
          style={{ boxShadow: `0 0 10px ${t.glow}` }}
        />
      </span>
    </Link>
  );
};

export default PioneerNavLink;
